import type { DefaultTheme } from 'styled-components';
import { ThemeProvider as StyledComponentsThemeProvider } from 'styled-components';

import type { GlobalStylesProps } from './GlobalStyles';
import GlobalStyles from './GlobalStyles';

/**
 * Props for the `ThemeProvider` component.
 *
 * @property theme - The theme object conforming to the `DefaultTheme` interface.
 * @property fontBaseUrl - Optional base URL for loading fonts, derived from `GlobalStylesProps['fontBaseUrl']`.
 * @property disableGlobalStyles - Optional flag to disable the injection of global styles. Defaults to `false`.
 * @property disableVendorPrefixes - Optional flag to disable vendor prefixing in styles. Defaults to `false`.  Deprecated - In Styled Components 6, vendor prefixes are omitted by default.
 * @property children - The React node(s) to be rendered within the provider.
 */
export interface Props {
  theme: DefaultTheme;
  fontBaseUrl?: GlobalStylesProps['fontBaseUrl'];
  disableGlobalStyles?: boolean;
  disableVendorPrefixes?: boolean;
  children: React.ReactNode;
}

const ThemeProvider = ({
  children,
  theme,
  fontBaseUrl = '/fonts',
  disableGlobalStyles = false,
  disableVendorPrefixes,
}: Props): React.JSX.Element => {
  if (disableVendorPrefixes != null) {
    console.warn(
      'The disableVendorPrefixes prop is deprecated. Vendor prefixes are now omitted by default in Styled Components',
    );
  }

  return (
    <StyledComponentsThemeProvider theme={theme}>
      {!disableGlobalStyles && (
        <GlobalStyles
          theme={theme}
          fontBaseUrl={fontBaseUrl}
          darkModeStrategy={theme.darkModeStrategy}
        />
      )}
      {children}
    </StyledComponentsThemeProvider>
  );
};

export default ThemeProvider;
